/* eslint-disable quote-props */
/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import UserModel from '../models/user.model';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private currentUser$: BehaviorSubject<UserModel|null> = new BehaviorSubject<UserModel|null>(null);
  private currentToken$: BehaviorSubject<string|null> = new BehaviorSubject<string|null>(null);

  constructor(
    private httpClient: HttpClient,
    private storageService: StorageService
  ) {
    this.loadStoredUser();
  }

  user() {
    const currentUser$ = this.userObservable();
    return currentUser$.getValue();
  }

  userObservable() {
    if (!!this.currentUser$.getValue() === false) { this.loadStoredUser(); }
    return this.currentUser$;
  }

  token() {
    const currentToken$ = this.tokenObservable();
    return currentToken$.getValue();
  }

  tokenObservable() {
    if (!!this.currentToken$.getValue() === false) { this.loadStoredToken(); }
    return this.currentToken$;
  }

  async userLogin(login: string = null, password: string): Promise<{ token: string; user: UserModel }|null> {
    return new Promise((resolve) => {
      const url = `${environment.apiBaseUrl}/api/auth/user/login`;
      this.httpClient.post(url, { login, password }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          // 'Authorization': `Bearer ${this.token()}` // Not required
        }
      })
      .toPromise()
      .then((response: any) => {
        if (!!response.data === false || response.error) {
          console.log(`${this.constructor.name}.userLogin() - then error`, response);
          return resolve(null);
        }

        // Convert object to model
        response.data.user = UserModel.fromObjectWithSnakeCase(response.data.user, new UserModel());

        // Local storage data
        this.storeUser(response.data.user);
        this.storeToken(response.data.token);

        return resolve(response.data);
      })
      .catch((error: any) => {
        console.log(`${this.constructor.name}.userLogin() - catch error`, error);
        return resolve(null);
      });
    });
  }

  async userLogout(): Promise<UserModel|null> {
    return new Promise((resolve) => {
      const url = `${environment.apiBaseUrl}/api/auth/user/logout`;

      this.httpClient.post(url, null, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.token()}`
        }
      })
      .toPromise()
      .then((response: any) => {
        if (!!response.data === false || response.error) {
          console.log(`${this.constructor.name}.userLogout() - then error`, response);
          return resolve(null);
        }

        // Convert object to model
        response.data = UserModel.fromObjectWithSnakeCase(response.data, new UserModel());

        // Local storage data
        this.deleteStoredToken();
        this.deleteStoredUser();

        return resolve(response.data);
      })
      .catch((error: any) => {
        console.log(`${this.constructor.name}.userLogout() - catch error`, error);

        // Local storage data
        this.deleteStoredToken();
        this.deleteStoredUser();

        return resolve(null);
      });

    });
  }

  async userCreate(data: UserModel, loginAfterCreation: boolean = false) {
    return new Promise((resolve) => {
      const url = `${environment.apiBaseUrl}/api/auth/user`;

      this.httpClient.post(url, data.toObjectWithSnakeCase(), {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.token()}`
        }
      })
      .toPromise()
      .then(async (response: any) => {
        if (!!response.data === false || response.error) {
          console.log(`${this.constructor.name}.userCreate() - then error`, response);
          return resolve(null);
        }

        // Convert object to model
        response.data = UserModel.fromObjectWithSnakeCase(response.data, new UserModel());

        // Login user
        if (!!loginAfterCreation) {
          await this.userLogin((!!data.email ? data.email : data.name), data.password);
        }

        return resolve(response.data);
      })
      .catch((error: any) => {
        console.log(`${this.constructor.name}.userCreate() - catch error`, error);
        return resolve(null);
      });

    });
  }

  async userUpdate(data: UserModel) {
    return new Promise((resolve) => {
      const url = `${environment.apiBaseUrl}/api/auth/user/${data.id}`;

      this.httpClient.patch(url, data.toObjectWithSnakeCase(), {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.token()}`
        }
      })
      .toPromise()
      .then(async (response: any) => {
        if (!!response.data === false || response.error) {
          console.log(`${this.constructor.name}.userUpdate() - then error`, response);
          return resolve(null);
        }

        // Convert object to model
        response.data = UserModel.fromObjectWithSnakeCase(response.data, new UserModel());

        // Local storage data
        this.storeUser(response.data);

        return resolve(response.data);
      })
      .catch((error: any) => {
        console.log(`${this.constructor.name}.userUpdate() - catch error`, error);
        return resolve(null);
      });
    });
  }

  async userDelete(id: number, isCurrentUser: boolean = false) {
    return new Promise((resolve) => {
      const url = `${environment.apiBaseUrl}/api/auth/user/${id}`;

      this.httpClient.delete(url, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.token()}`
        }
      })
      .toPromise()
      .then(async (response: any) => {
        if (!!response.data === false || response.error) {
          console.log(`${this.constructor.name}.userUpdate() - then error`, response);
          return resolve(null);
        };

        // Local storage data
        if (!!isCurrentUser) {
          this.deleteStoredUser();
          this.deleteStoredToken();
        }

        return resolve(response.data);
      })
      .catch((error: any) => {
        console.log(`${this.constructor.name}.userUpdate() - catch error`, error);
        return resolve(null);
      });
    });
  }

//#region Local Storage
  private storeUser(data: UserModel) {
    this.storageService.setObject('currentUser', data);
    this.loadStoredUser();
  }

  private storeToken(token: string) {
    this.storageService.setItem('currentToken', token);
    this.loadStoredToken();
  }

  private loadStoredUser() {
    const loggedUser = this.storageService.getObject('currentUser');
    this.currentUser$.next(!!loggedUser ? UserModel.fromObject(loggedUser, new UserModel()) : null);
  }

  private loadStoredToken() {
    const currentToken = this.storageService.getItem('currentToken');
    this.currentToken$.next(!!currentToken ? currentToken : null);
  }

  private deleteStoredUser() {
    this.storageService.deleteKey('currentUser');
    this.currentUser$.next(null);
  }

  private deleteStoredToken() {
    this.storageService.deleteKey('currentToken');
    this.currentToken$.next(null);
  }
//#endregion Local Storage
}
